/**
 * DTO модуля ОСНОВА.Кадры (1:1 с backend `osnova-kadry`, snake_case).
 * Контракт — `docs/kadry/openapi.json`.
 */

export type KadryRole = 'hr_admin' | 'recruiter' | 'requester' | 'auditor' | 'sys_admin';

export type KadryOp =
  | 'request_create'
  | 'request_manage'
  | 'profile_approve'
  | 'search_run'
  | 'shortlist_view'
  | 'candidate_view_full'
  | 'paid_action'
  | 'match_status_change'
  | 'match_rate'
  | 'merge_split'
  | 'import_former_employees'
  | 'campaign_manage'
  | 'bot_content_prepare'
  | 'bot_bundle_approve'
  | 'bot_rules_global'
  | 'bot_rules_request'
  | 'bot_pause_request'
  | 'bot_pause_global'
  | 'bot_full_stop'
  | 'conversations_reply'
  | 'metrics_view'
  | 'audit_log_view'
  | 'dsr_view'
  | 'dsr_manage'
  | 'admin_settings'
  | 'export'
  | 'tech_settings'
  | 'sse_feed'
  | 'attachments_view'
  | 'break_glass_request'
  | 'break_glass_approve';

export type KadryModuleStatus =
  | { state: 'disabled' }
  | { state: 'unavailable'; reason: string }
  | { state: 'ready' };

export interface KadryInfo {
  agent_code: 'kadry';
  title: string;
  status: KadryModuleStatus;
  roles: KadryRole[];
  capabilities: KadryOp[];
  sees_pii: boolean;
}

/** Уровень доступа пользователя к объекту (`none` наружу не приходит — это 404). */
export type KadryAccess = 'none' | 'safe' | 'full';

// ===== Заявки (ТЗ §3 С1, Прил. Г «Заявка») =====

export type KadryRequestStatus = 'draft' | 'active' | 'paused' | 'closed';

export type KadryAssigneeRole = 'recruiter' | 'requester';

export interface KadryAssignee {
  user_id: string;
  role: KadryAssigneeRole;
  assigned_at: string;
}

/** `HiringRequestView`: `raw_text` отдаётся только при полном доступе или автору/заказчику. */
/** Откуда заявка: создана человеком или из вакансии hh.ru (Р22). */
export type KadryRequestOrigin = 'manual' | 'hh_import';

export interface KadryHiringRequest {
  id: string;
  title: string;
  raw_text?: string | null;
  work_site_id?: string | null;
  department?: string | null;
  requester_id?: string | null;
  status: KadryRequestStatus;
  headcount: number;
  budget_rub?: number | null;
  /** Период повторного поиска, часов. */
  schedule_hours: number;
  active_profile_version?: number | null;
  version: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  activated_at?: string | null;
  closed_at?: string | null;
  origin: KadryRequestOrigin;
  assignees: KadryAssignee[];
  access: KadryAccess;
}

/** `CreateRequest`. */
export interface KadryCreateRequest {
  title: string;
  raw_text?: string | null;
  work_site_id?: string | null;
  department?: string | null;
  requester_id?: string | null;
  headcount?: number | null;
  budget_rub?: number | null;
  schedule_hours?: number | null;
}

/** `PatchRequest`: отсутствующее поле не меняется, `null` очищает (где допустимо). */
export interface KadryPatchRequest {
  title?: string | null;
  raw_text?: string | null;
  work_site_id?: string | null;
  department?: string | null;
  requester_id?: string | null;
  headcount?: number | null;
  budget_rub?: number | null;
  schedule_hours?: number | null;
  /** Переход состояния по машине состояний Прил. Г. */
  status?: KadryRequestStatus | null;
  /** Причина перехода (в историю). */
  reason?: string | null;
}

export interface KadryListRequestsQuery {
  status?: KadryRequestStatus;
  /** Только заявки, где я автор, заказчик или назначен. */
  mine?: boolean;
  limit?: number;
  offset?: number;
}

export interface KadryStatusHistoryEntry {
  from_status?: KadryRequestStatus | null;
  to_status: KadryRequestStatus;
  actor_id?: string | null;
  reason?: string | null;
  at: string;
}

// ===== Профиль поиска (Прил. Е `SearchProfile`) =====
// Полей про возраст, пол, место проживания в схеме нет (§1.6) — и не добавлять.

export type KadrySourceId = 'hh' | 'avito' | 'superjob' | 'trudvsem' | 'internal';

export type KadryScheduleKind = 'rotation' | 'shift' | 'full_time' | 'part_time' | 'flexible';

export interface KadrySchedule {
  type: KadryScheduleKind;
  /** Описание смены: «2/2», «день/ночь», «5/2». */
  shift?: string | null;
  /** Длительность вахты в днях. */
  rotation_days?: number | null;
}

export interface KadrySearchProfile {
  position: string;
  synonyms?: string[];
  skills?: { required?: string[]; optional?: string[] };
  permits?: { required?: string[] };
  /** Минимум лет **в профильной роли** (не общий стаж — §1.6). */
  experience?: { years_in_role_min?: number | null };
  work_site_id?: string | null;
  schedule?: KadrySchedule | null;
  readiness_required?: { rotation?: boolean; relocation?: boolean; business_trips?: boolean };
  /** Рублей в месяц «на руки». */
  pay?: { from?: number | null; to?: number | null } | null;
  sources?: KadrySourceId[];
}

export type KadryProfileStatus = 'draft' | 'approved';

export type KadryProfileOrigin = 'llm' | 'rules' | 'copy' | 'manual';

export interface KadryProfileVersion {
  request_id: string;
  version: number;
  status: KadryProfileStatus;
  origin: KadryProfileOrigin;
  /** Ревизия черновика для If-Match. */
  revision: number;
  profile: KadrySearchProfile;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  approved_by?: string | null;
  approved_at?: string | null;
}

/** `NewProfileDraft`: как создать черновик. */
export type KadryNewProfileDraft =
  | { mode: 'manual'; profile: KadrySearchProfile }
  | { mode: 'copy'; from_version: number }
  | { mode: 'parse' };

// ===== Справочник объектов =====

export interface KadryWorkSite {
  id: string;
  name: string;
  address?: string | null;
  region_code?: string | null;
  /** IANA-зона, например `Asia/Yekaterinburg`. */
  timezone: string;
  field_site_id?: string | null;
  active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface KadryWorkSiteInput {
  name: string;
  address?: string | null;
  region_code?: string | null;
  timezone: string;
  field_site_id?: string | null;
  active?: boolean;
}

// ===== Фичегейты (ADR-0019 §3) =====

export type KadryGate =
  | 'module'
  | 'source.hh'
  | 'source.avito'
  | 'source.superjob'
  | 'source.trudvsem'
  | 'channel.telegram'
  | 'channel.email'
  | 'channel.platform_invite'
  | 'llm.external'
  | 'llm.external_candidate_text'
  | 'import.former_employees'
  | 'campaigns'
  | 'bot.autooffer'
  | 'bot.autooffer_platform_search'
  | 'export'
  | 'mail.oneclick_unsubscribe'
  | 'channel.max'
  | 'source.rabota'
  | 'channel.sms';

/** Настройки автопредложений: порог отбора и суточные лимиты.
 *
 *  Порог отсекает кандидатов, которые почти не отвечают на предложения, —
 *  контакт площадки они тратят наравне с сильными. Лимиты не дают агенту
 *  выжечь оплаченный пакет контактов за один день. */
export interface KadryAutoofferSettings {
  /** Балл 0–100, с которого предложение уходит без кадровика. */
  min_score: number;
  /** Потолок автопредложений на одну заявку за сутки (обычный режим). */
  daily_limit_request: number;
  /** Потолок по всем заявкам за сутки. В массовом наборе не применяется. */
  daily_limit_total: number;
  /** Массовый набор: считаем лимит только по каждой вакансии отдельно. */
  mass_hiring: boolean;
  /** Потолок на одну вакансию за сутки в режиме массового набора. */
  mass_daily_limit_request: number;
  updated_by?: string | null;
  updated_at: string;
  /** Версия для защиты от затирания чужой правки. */
  version: number;
}

export interface KadryAutoofferSettingsBody {
  min_score: number;
  daily_limit_request: number;
  daily_limit_total: number;
  mass_hiring: boolean;
  mass_daily_limit_request: number;
  version: number;
}

export interface KadryGateState {
  gate: KadryGate;
  title: string;
  /** Организационное условие, после которого гейт можно включать. */
  condition: string;
  enabled: boolean;
  changed_by?: string | null;
  changed_at?: string | null;
  comment?: string | null;
  version: number;
}

export interface KadrySetGateBody {
  enabled: boolean;
  /** Основание включения (ссылка на решение, договор) — в аудит. */
  comment?: string | null;
}

// ===== Журнал аудита (без ПДн) =====

export interface KadryAuditRecord {
  id: number;
  at: string;
  actor_kind: string;
  actor_id?: string | null;
  /** ФИО исполнителя-сотрудника (`actor_kind = user`); для системы и кандидатов — `null`. */
  actor_name?: string | null;
  action: string;
  entity: string;
  entity_id?: string | null;
  result: string;
  meta: Record<string, unknown>;
}

export interface KadryAuditQuery {
  /** Префикс действия, например `kadry.request.` */
  action?: string;
  entity?: string;
  entity_id?: string;
  actor_id?: string;
  /** Курсор: записи с id меньше указанного. */
  before_id?: number;
  limit?: number;
}

// ===== Справочник сотрудников для назначения =====

export interface KadryStaffMember {
  user_id: string;
  full_name: string;
  department?: string | null;
  /** Роли пользователя в модуле. */
  roles: KadryRole[];
}

/** Ошибка API модуля: `{ error: { code, message, details } }`. */
export interface KadryApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

export const KADRY_ROLE_LABELS: Record<KadryRole, string> = {
  hr_admin: 'Администратор подбора',
  recruiter: 'Рекрутер',
  requester: 'Заказчик подбора',
  auditor: 'Аудитор',
  sys_admin: 'Технический администратор',
};

/** Машинный код ошибки модуля (`version_conflict`, `feature_disabled`, …), если он есть. */
export function kadryErrorCode(err: unknown): string | undefined {
  const data = (err as { data?: unknown } | undefined)?.data;
  const e = data && typeof data === 'object' ? (data as Partial<KadryApiErrorBody>).error : undefined;
  if (e && typeof e === 'object' && typeof e.code === 'string') return e.code;
  return undefined;
}

/** `error.details` ответа модуля (структурированные подробности без ПДн). */
export function kadryErrorDetails(err: unknown): Record<string, unknown> {
  const data = (err as { data?: unknown } | undefined)?.data;
  const e = data && typeof data === 'object' ? (data as Partial<KadryApiErrorBody>).error : undefined;
  const d = e && typeof e === 'object' ? e.details : undefined;
  return d && typeof d === 'object' ? (d as Record<string, unknown>) : {};
}

/** Ошибка говорит о состоянии модуля целиком (выключен или недоступен). */
export function isKadryModuleStateError(err: unknown): boolean {
  const code = kadryErrorCode(err);
  return (
    code === 'module_unavailable' ||
    (code === 'feature_disabled' && kadryErrorDetails(err).gate === 'module')
  );
}

/** Подписи полей для ошибок `validation_error` (`details.field`). */
const KADRY_FIELD_LABELS: Record<string, string> = {
  title: 'Название',
  raw_text: 'Текст заявки',
  headcount: 'Численность',
  budget_rub: 'Бюджет',
  schedule_hours: 'Период повторного поиска',
  work_site_id: 'Объект',
  requester_id: 'Заказчик',
  user_id: 'Сотрудник',
  reason: 'Причина',
  position: 'Должность',
  synonyms: 'Синонимы',
  'skills.required': 'Обязательные навыки',
  'skills.optional': 'Желательные навыки',
  'permits.required': 'Обязательные допуски',
  'experience.years_in_role_min': 'Лет в профильной роли',
  'schedule.shift': 'Смена',
  'schedule.rotation_days': 'Длительность вахты',
  pay: 'Оплата',
  'pay.from': 'Оплата от',
  'pay.to': 'Оплата до',
  sources: 'Источники',
  name: 'Название',
  address: 'Адрес',
  region_code: 'Код региона',
  timezone: 'Часовой пояс',
  comment: 'Комментарий',
  gate: 'Функция',
};

/** Текст ошибки API для пользователя (понимает формат модуля и платформы). */
export function kadryErrorMessage(err: unknown): string {
  const code = kadryErrorCode(err);
  if (code === 'validation_error') {
    const d = kadryErrorDetails(err);
    const label = typeof d.field === 'string' ? KADRY_FIELD_LABELS[d.field] : undefined;
    const reason = typeof d.reason === 'string' ? d.reason : undefined;
    if (label && reason) return `${label}: ${reason}`;
  }
  if (code === 'version_conflict') return 'Данные изменились, обновите страницу.';
  if (code === 'precondition_required') {
    return 'Не передана версия объекта. Обновите страницу и повторите.';
  }
  if (code === 'feature_disabled' && kadryErrorDetails(err).gate === 'module') {
    return 'Модуль «Кадры» выключен.';
  }
  const data = (err as { data?: unknown } | undefined)?.data as
    | KadryApiErrorBody
    | { error?: string }
    | undefined;
  const e = data && typeof data === 'object' ? (data as KadryApiErrorBody).error : undefined;
  if (e && typeof e === 'object' && typeof e.message === 'string') return e.message;
  if (typeof (data as { error?: string } | undefined)?.error === 'string') {
    return (data as { error: string }).error;
  }
  const status = (err as { status?: unknown } | undefined)?.status;
  if (status === 'FETCH_ERROR') return 'Нет связи с сервером. Проверьте подключение.';
  return 'Не удалось выполнить запрос. Попробуйте ещё раз.';
}
